import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Pagination,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterListIcon from "@mui/icons-material/FilterList";
import DownloadIcon from "@mui/icons-material/Download";
import HistoryIcon from "@mui/icons-material/History";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import BlockIcon from "@mui/icons-material/Block";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import {
  useGetAdminInvitesVisitsQuery,
  GetAdminInvitesVisitsApiResponse,
  useGetPropertiesQuery,
  usePatchInviteByIdRevokeMutation,
} from "../../redux/residentPortalApi";
import { usePatchInviteByIdResendMutation } from "../../redux/inviteResendApi";
import config from "../../config";
import { visitorTypeOptions } from "../vms/addVisitorModel";
import VisitLogTimeline from "../vms/VisitLogTimeline";
import { isOverstaying } from "../../utils/visitStatus";

const LEGACY_VISITOR_TYPES = [
  { value: "bnb", label: "BnB" },
  { value: "company", label: "Company" },
  { value: "visitor", label: "Visitor (legacy)" },
];
const ALL_VISITOR_TYPE_FILTER_OPTIONS = [
  ...visitorTypeOptions,
  ...LEGACY_VISITOR_TYPES,
];

const ROWS_PER_PAGE = 15;
const FILTER_PRESETS_STORAGE_KEY = "adminInvitesVisitsFilterPresets";

type FilterPreset = {
  search: string;
  development: string;
  phase: string;
  propertyId: string;
  propertyNumber: string;
  visitorType: string;
  invitedByType: string;
  scannedFilter: string;
  revokedFilter: string;
  fromDate: string;
  toDate: string;
};

type FilterPresetMap = Record<string, FilterPreset>;
type AdminInviteRow = GetAdminInvitesVisitsApiResponse[number];

const STATUS_META: Record<
  string,
  {
    label: string;
    color: "default" | "success" | "info" | "warning";
    icon: React.ReactElement;
  }
> = {
  checked_in: {
    label: "Checked In",
    color: "success",
    icon: <LoginIcon fontSize="small" />,
  },
  checked_out: {
    label: "Checked Out",
    color: "info",
    icon: <LogoutIcon fontSize="small" />,
  },
  not_arrived: {
    label: "Not Arrived",
    color: "default",
    icon: <HowToRegIcon fontSize="small" />,
  },
};

const getStatusMeta = (invite: AdminInviteRow) =>
  STATUS_META[invite.currentStatus ?? "not_arrived"] ?? STATUS_META.not_arrived;

const toYesNo = (value?: boolean) => {
  if (value === true) return "Yes";
  if (value === false) return "No";
  return "-";
};

const formatDateTime = (value?: string) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const escapeCsv = (value: unknown) => {
  if (value === null || value === undefined) return "";
  const text = String(value);
  if (/[",\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

// Default view loads only the last 7 days; users can widen the range via the date filters
const DEFAULT_LOOKBACK_DAYS = 7;
const getDefaultFromDate = () => {
  const date = new Date();
  date.setDate(date.getDate() - DEFAULT_LOOKBACK_DAYS);
  return date.toISOString().slice(0, 10);
};

const AdminInvitesVisits: React.FC = () => {
  const [search, setSearch] = useState("");
  const [development, setDevelopment] = useState("");
  const [phase, setPhase] = useState("");
  const [propertyId, setPropertyId] = useState("");
  const [propertyNumber, setPropertyNumber] = useState("");
  const [visitorType, setVisitorType] = useState("");
  const [invitedByType, setInvitedByType] = useState("");
  const [scannedFilter, setScannedFilter] = useState("");
  const [revokedFilter, setRevokedFilter] = useState("");
  const [fromDate, setFromDate] = useState(getDefaultFromDate());
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);
  const [presets, setPresets] = useState<FilterPresetMap>({});
  const [selectedPreset, setSelectedPreset] = useState("");
  const [presetName, setPresetName] = useState("");
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [activeActionInviteId, setActiveActionInviteId] = useState<
    string | null
  >(null);
  const [historyInvite, setHistoryInvite] = useState<AdminInviteRow | null>(
    null,
  );

  const [revokeInvite] = usePatchInviteByIdRevokeMutation();
  const [resendInvite] = usePatchInviteByIdResendMutation();

  const scanned = scannedFilter === "" ? undefined : scannedFilter === "true";
  const revoked = revokedFilter === "" ? undefined : revokedFilter === "true";
  const parsedPropertyNumber =
    propertyNumber.trim() === "" ? undefined : Number(propertyNumber);

  const { data: allProperties = [] } = useGetPropertiesQuery();

  const {
    data: invites = [],
    isFetching,
    isError,
    refetch,
  } = useGetAdminInvitesVisitsQuery({
    development: development || undefined,
    phase: phase || undefined,
    propertyId: propertyId || undefined,
    propertyNumber:
      typeof parsedPropertyNumber === "number" &&
      !Number.isNaN(parsedPropertyNumber)
        ? parsedPropertyNumber
        : undefined,
    visitorType: visitorType || undefined,
    invitedByType: invitedByType || undefined,
    scanned,
    revoked,
    fromDate: fromDate || undefined,
    toDate: toDate || undefined,
    search: search.trim() || undefined,
  });

  const developments = useMemo(
    () =>
      [
        ...new Set(allProperties.map((property) => property.development)),
      ].sort(),
    [allProperties],
  );

  const phases = useMemo(() => {
    const filtered = allProperties.filter((property) =>
      development ? property.development === development : true,
    );
    return [...new Set(filtered.map((property) => property.phase))].sort();
  }, [allProperties, development]);

  const propertiesForSelectors = useMemo(() => {
    const parseUnitNumber = (unitNumber?: string | number) => {
      const value = String(unitNumber ?? "").trim();
      if (!value) return Number.MAX_SAFE_INTEGER;
      const parsed = Number(value.replace(/[^\d.-]/g, ""));
      return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
    };

    return allProperties
      .filter((property) =>
        development ? property.development === development : true,
      )
      .filter((property) => (phase ? property.phase === phase : true))
      .sort((a, b) => {
        if (a.development !== b.development) {
          return a.development.localeCompare(b.development);
        }
        if (a.phase !== b.phase) {
          return a.phase.localeCompare(b.phase);
        }
        return parseUnitNumber(a.unitNumber) - parseUnitNumber(b.unitNumber);
      });
  }, [allProperties, development, phase]);

  const propertiesById = useMemo(() => {
    return new Map(
      allProperties
        .filter((property) => !!property.id)
        .map((property) => [property.id as string, property]),
    );
  }, [allProperties]);

  const summaryStats = useMemo(() => {
    const total = invites.length;
    const checkedIn = invites.filter(
      (invite) => invite.currentStatus === "checked_in",
    ).length;
    const checkedOut = invites.filter(
      (invite) => invite.currentStatus === "checked_out",
    ).length;
    const revokedCount = invites.filter((invite) => invite.revoked).length;
    const consentConfirmed = invites.filter(
      (invite) => invite.consentConfirmed,
    ).length;
    const overstaying = invites.filter(isOverstaying).length;
    return {
      total,
      checkedIn,
      checkedOut,
      revokedCount,
      consentConfirmed,
      overstaying,
    };
  }, [invites]);

  const getInvitePropertyMeta = (invite: AdminInviteRow) => {
    const property = invite.propertyId
      ? propertiesById.get(invite.propertyId)
      : undefined;

    return {
      development: invite.development || property?.development || "-",
      phase: invite.phase || property?.phase || "-",
      unitNumber: invite.propertyNumber ?? property?.unitNumber ?? "-",
    };
  };

  const paginatedInvites = useMemo(() => {
    const start = (page - 1) * ROWS_PER_PAGE;
    return invites.slice(start, start + ROWS_PER_PAGE);
  }, [invites, page]);

  const totalPages = Math.max(1, Math.ceil(invites.length / ROWS_PER_PAGE));

  const currentFilters: FilterPreset = {
    search,
    development,
    phase,
    propertyId,
    propertyNumber,
    visitorType,
    invitedByType,
    scannedFilter,
    revokedFilter,
    fromDate,
    toDate,
  };

  useEffect(() => {
    try {
      const stored = localStorage.getItem(FILTER_PRESETS_STORAGE_KEY);
      if (!stored) return;
      const parsed = JSON.parse(stored) as FilterPresetMap;
      if (parsed && typeof parsed === "object") {
        setPresets(parsed);
      }
    } catch (_error) {
      setPresets({});
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(FILTER_PRESETS_STORAGE_KEY, JSON.stringify(presets));
  }, [presets]);

  useEffect(() => {
    setPage(1);
  }, [
    search,
    development,
    phase,
    propertyId,
    propertyNumber,
    visitorType,
    invitedByType,
    scannedFilter,
    revokedFilter,
    fromDate,
    toDate,
  ]);

  const clearFilters = () => {
    setSearch("");
    setDevelopment("");
    setPhase("");
    setPropertyId("");
    setPropertyNumber("");
    setVisitorType("");
    setInvitedByType("");
    setScannedFilter("");
    setRevokedFilter("");
    setFromDate(getDefaultFromDate());
    setToDate("");
  };

  const saveCurrentPreset = () => {
    const name = presetName.trim();
    if (!name) return;
    setPresets((previous) => ({
      ...previous,
      [name]: { ...currentFilters },
    }));
    setSelectedPreset(name);
    setPresetName("");
  };

  const applySelectedPreset = () => {
    if (!selectedPreset) return;
    const preset = presets[selectedPreset];
    if (!preset) return;

    setSearch(preset.search);
    setDevelopment(preset.development);
    setPhase(preset.phase);
    setPropertyId(preset.propertyId);
    setPropertyNumber(preset.propertyNumber);
    setVisitorType(preset.visitorType);
    setInvitedByType(preset.invitedByType);
    setScannedFilter(preset.scannedFilter);
    setRevokedFilter(preset.revokedFilter);
    setFromDate(preset.fromDate);
    setToDate(preset.toDate);
  };

  const deleteSelectedPreset = () => {
    if (!selectedPreset) return;
    setPresets((previous) => {
      const next = { ...previous };
      delete next[selectedPreset];
      return next;
    });
    setSelectedPreset("");
  };

  const exportToCsv = () => {
    const headers = [
      "Development",
      "Phase",
      "PropertyNumber",
      "PropertyId",
      "VisitorName",
      "VisitorType",
      "InvitedByType",
      "Purpose",
      "VisitorEmail",
      "VisitorWhatsApp",
      "IsMultiDay",
      "VisitDateTime",
      "StartDate",
      "EndDate",
      "Scanned",
      "ScannedAt",
      "ScanCount",
      "Revoked",
      "AccessCode",
      "CreatedByUserId",
      "CurrentStatus",
      "LastScannedAt",
      "ConsentConfirmed",
    ];

    const rows = invites.map((invite) => [
      getInvitePropertyMeta(invite).development,
      getInvitePropertyMeta(invite).phase,
      getInvitePropertyMeta(invite).unitNumber,
      invite.propertyId,
      invite.visitorName,
      invite.visitorType,
      invite.invitedByType,
      invite.purpose,
      invite.visitorEmail,
      invite.visitorWhatsApp,
      invite.isMultiDay,
      invite.visitDateTime,
      invite.startDate,
      invite.endDate,
      invite.scanned,
      invite.scannedAt,
      invite.scanCount,
      invite.revoked,
      invite.accessCode,
      invite.createdByUserId,
      invite.currentStatus,
      invite.lastScannedAt,
      invite.consentConfirmed,
    ]);

    const csv = [
      headers.map(escapeCsv).join(","),
      ...rows.map((row) => row.map(escapeCsv).join(",")),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const dateSuffix = new Date().toISOString().slice(0, 10);
    link.href = url;
    link.download = `admin-invites-visits-${dateSuffix}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getInvitePassUrl = (inviteId?: string) => {
    if (!inviteId) return "";
    return `${config.CDN_URL}/invites/${inviteId}.png`;
  };

  const handleResendInvite = async (inviteId?: string) => {
    if (!inviteId) return;

    try {
      setActionError(null);
      setActionMessage(null);
      setActiveActionInviteId(inviteId);
      const response = await resendInvite({ id: inviteId }).unwrap();
      setActionMessage(response.message ?? "Invite resent successfully.");
      await refetch();
    } catch (error) {
      console.error("Failed to resend invite:", error);
      setActionError("Failed to resend invite. Please try again.");
    } finally {
      setActiveActionInviteId(null);
    }
  };

  const handleRevokeInvite = async (inviteId?: string) => {
    if (!inviteId) return;

    try {
      setActionError(null);
      setActionMessage(null);
      setActiveActionInviteId(inviteId);
      await revokeInvite({ id: inviteId }).unwrap();
      setActionMessage("Invite revoked successfully.");
      await refetch();
    } catch (error) {
      console.error("Failed to revoke invite:", error);
      setActionError("Failed to revoke invite. Please try again.");
    } finally {
      setActiveActionInviteId(null);
    }
  };

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  const handleDevelopmentChange = (event: SelectChangeEvent) => {
    setDevelopment(event.target.value);
    setPhase("");
    setPropertyId("");
    setPropertyNumber("");
  };

  const handlePhaseChange = (event: SelectChangeEvent) => {
    setPhase(event.target.value);
    setPropertyId("");
    setPropertyNumber("");
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Invites And Visits
        </Typography>
        <Typography variant="body1" color="text.secondary">
          View all invites across properties with admin-level filtering, live
          check-in/check-out status, and consent tracking.
        </Typography>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          {
            label: "Total Invites",
            value: summaryStats.total,
            icon: <HowToRegIcon fontSize="small" />,
            color: "#345678",
          },
          {
            label: "Currently Checked In",
            value: summaryStats.checkedIn,
            icon: <LoginIcon fontSize="small" />,
            color: "#2e7d32",
          },
          {
            label: "Checked Out",
            value: summaryStats.checkedOut,
            icon: <LogoutIcon fontSize="small" />,
            color: "#0288d1",
          },
          {
            label: "Overstaying",
            value: summaryStats.overstaying,
            icon: <WarningAmberIcon fontSize="small" />,
            color: "#e65100",
          },
          {
            label: "Revoked",
            value: summaryStats.revokedCount,
            icon: <BlockIcon fontSize="small" />,
            color: "#c62828",
          },
          {
            label: "Consent Confirmed",
            value: summaryStats.consentConfirmed,
            icon: <VerifiedUserIcon fontSize="small" />,
            color: "#6a1b9a",
          },
        ].map((stat) => (
          <Grid item xs={6} md={2} key={stat.label}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: "1px solid #e8ecef",
                borderRadius: 2,
                height: "100%",
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ color: stat.color, mb: 0.5 }}
              >
                {stat.icon}
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: ".04em",
                  }}
                >
                  {stat.label}
                </Typography>
              </Stack>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {stat.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Card elevation={3} sx={{ mb: 3 }}>
        <CardContent>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <FilterListIcon fontSize="small" color="action" />
              <Typography variant="h6">Filters</Typography>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={exportToCsv}
                disabled={isFetching || invites.length === 0}
              >
                Export CSV
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={
                  isFetching ? <CircularProgress size={14} /> : <RefreshIcon />
                }
                onClick={() => refetch()}
                disabled={isFetching}
              >
                Refresh
              </Button>
            </Stack>
          </Stack>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Visitor name, contact, access code, purpose"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Development</InputLabel>
                <Select
                  value={development}
                  label="Development"
                  onChange={handleDevelopmentChange}
                >
                  <MenuItem value="">All</MenuItem>
                  {developments.map((item) => (
                    <MenuItem key={item} value={item}>
                      {item}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Phase</InputLabel>
                <Select
                  value={phase}
                  label="Phase"
                  onChange={handlePhaseChange}
                >
                  <MenuItem value="">All</MenuItem>
                  {phases.map((item) => (
                    <MenuItem key={item} value={item}>
                      {item}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Property Number"
                value={propertyNumber}
                onChange={(event) => setPropertyNumber(event.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Property</InputLabel>
                <Select
                  value={propertyId}
                  label="Property"
                  onChange={(event) => setPropertyId(event.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {propertiesForSelectors.map((property) => (
                    <MenuItem key={property.id} value={property.id}>
                      {`${property.development} / ${property.phase} / ${property.unitNumber}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Visitor Type</InputLabel>
                <Select
                  value={visitorType}
                  label="Visitor Type"
                  onChange={(event) => setVisitorType(event.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {ALL_VISITOR_TYPE_FILTER_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Invited By</InputLabel>
                <Select
                  value={invitedByType}
                  label="Invited By"
                  onChange={(event) => setInvitedByType(event.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="owner">Owner</MenuItem>
                  <MenuItem value="bnb_guest">BnB Guest</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Scanned</InputLabel>
                <Select
                  value={scannedFilter}
                  label="Scanned"
                  onChange={(event) => setScannedFilter(event.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Revoked</InputLabel>
                <Select
                  value={revokedFilter}
                  label="Revoked"
                  onChange={(event) => setRevokedFilter(event.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="From Date"
                type="date"
                value={fromDate}
                onChange={(event) => setFromDate(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="To Date"
                type="date"
                value={toDate}
                onChange={(event) => setToDate(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack
                direction="row"
                spacing={1}
                sx={{ height: "100%" }}
                alignItems="center"
              >
                <Button variant="text" onClick={clearFilters}>
                  Clear Filters
                </Button>
                <Typography variant="caption" color="text.secondary">
                  Showing the last {DEFAULT_LOOKBACK_DAYS} days by default -
                  adjust From/To Date for a different period.
                </Typography>
              </Stack>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Preset Name"
                value={presetName}
                onChange={(event) => setPresetName(event.target.value)}
                placeholder="e.g. Pokugara unscanned"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Saved Preset</InputLabel>
                <Select
                  value={selectedPreset}
                  label="Saved Preset"
                  onChange={(event) => setSelectedPreset(event.target.value)}
                >
                  <MenuItem value="">Select preset</MenuItem>
                  {Object.keys(presets)
                    .sort((a, b) => a.localeCompare(b))
                    .map((name) => (
                      <MenuItem key={name} value={name}>
                        {name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={5}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ height: "100%" }}
              >
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={saveCurrentPreset}
                  disabled={!presetName.trim()}
                >
                  Save Preset
                </Button>
                <Button
                  variant="outlined"
                  onClick={applySelectedPreset}
                  disabled={!selectedPreset}
                >
                  Apply Preset
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={deleteSelectedPreset}
                  disabled={!selectedPreset}
                >
                  Delete Preset
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load invites and visits.
        </Alert>
      )}

      {actionMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {actionMessage}
        </Alert>
      )}

      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {actionError}
        </Alert>
      )}

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ border: "1px solid #e8ecef" }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
              <TableCell>Date / Period</TableCell>
              <TableCell>Development</TableCell>
              <TableCell>Phase</TableCell>
              <TableCell>Property #</TableCell>
              <TableCell>Visitor</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Invited By</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Consent</TableCell>
              <TableCell>Purpose</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isFetching ? (
              <TableRow>
                <TableCell colSpan={12} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : paginatedInvites.length === 0 ? (
              <TableRow>
                <TableCell colSpan={12} align="center" sx={{ py: 6 }}>
                  <Typography color="text.secondary">
                    No invites found for these filters.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedInvites.map((invite) =>
                (() => {
                  const propertyMeta = getInvitePropertyMeta(invite);
                  return (
                    <TableRow key={invite.id} hover>
                      <TableCell>
                        {invite.isMultiDay
                          ? `${formatDateTime(
                              invite.startDate,
                            )} - ${formatDateTime(invite.endDate)}`
                          : formatDateTime(invite.visitDateTime)}
                      </TableCell>
                      <TableCell>{propertyMeta.development}</TableCell>
                      <TableCell>{propertyMeta.phase}</TableCell>
                      <TableCell>{propertyMeta.unitNumber}</TableCell>
                      <TableCell>{invite.visitorName || "-"}</TableCell>
                      <TableCell>{invite.visitorType || "-"}</TableCell>
                      <TableCell>{invite.invitedByType || "-"}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          <Chip
                            size="small"
                            icon={getStatusMeta(invite).icon}
                            label={getStatusMeta(invite).label}
                            color={getStatusMeta(invite).color}
                            variant={
                              getStatusMeta(invite).color === "default"
                                ? "outlined"
                                : "filled"
                            }
                          />
                          {invite.revoked && (
                            <Chip size="small" label="Revoked" color="error" />
                          )}
                          {isOverstaying(invite) && (
                            <Chip
                              size="small"
                              icon={<WarningAmberIcon fontSize="small" />}
                              label="Overstay"
                              color="warning"
                            />
                          )}
                        </Stack>
                        {invite.lastScannedAt && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mt: 0.5 }}
                          >
                            {formatDateTime(invite.lastScannedAt)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={toYesNo(invite.consentConfirmed)}
                          color={
                            invite.consentConfirmed ? "success" : "default"
                          }
                          variant={
                            invite.consentConfirmed ? "filled" : "outlined"
                          }
                        />
                      </TableCell>
                      <TableCell>{invite.purpose || "-"}</TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {invite.visitorEmail || "-"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {invite.visitorWhatsApp || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<HistoryIcon fontSize="small" />}
                            onClick={() => setHistoryInvite(invite)}
                            disabled={!invite.id}
                          >
                            History
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            component="a"
                            href={getInvitePassUrl(invite.id)}
                            target="_blank"
                            rel="noreferrer"
                            download
                            disabled={!invite.id}
                          >
                            Download Pass
                          </Button>
                          {!invite.revoked && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleResendInvite(invite.id)}
                              disabled={
                                !invite.id || activeActionInviteId === invite.id
                              }
                            >
                              Resend
                            </Button>
                          )}
                          {!invite.revoked && (
                            <Button
                              size="small"
                              color="error"
                              variant="outlined"
                              onClick={() => handleRevokeInvite(invite.id)}
                              disabled={
                                !invite.id || activeActionInviteId === invite.id
                              }
                            >
                              Revoke
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })(),
              )
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={Boolean(historyInvite)}
        onClose={() => setHistoryInvite(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Visit History
          {historyInvite?.visitorName ? ` — ${historyInvite.visitorName}` : ""}
        </DialogTitle>
        <DialogContent dividers>
          <VisitLogTimeline inviteId={historyInvite?.id} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryInvite(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Showing {paginatedInvites.length} of {invites.length} records
        </Typography>
        <Pagination
          page={page}
          count={totalPages}
          onChange={handlePageChange}
          color="primary"
          size="small"
        />
      </Box>
    </Box>
  );
};

export default AdminInvitesVisits;
